import * as THREE from 'three';
import type { CelestialBody } from '../scene/CelestialBody';
import type { FlyControls } from './FlyControls';

export class TeleportController {
  private controls: FlyControls;
  private camera: THREE.PerspectiveCamera;
  private active = false;
  private t = 0;
  private duration = 1;
  private startPos = new THREE.Vector3();
  private endPos = new THREE.Vector3();
  private startQuat = new THREE.Quaternion();
  private endQuat = new THREE.Quaternion();
  private midPos = new THREE.Vector3();
  private tmp = new THREE.Vector3();
  private lookTarget = new THREE.Vector3();
  private onComplete?: () => void;

  constructor(controls: FlyControls, camera: THREE.PerspectiveCamera) {
    this.controls = controls;
    this.camera = camera;
  }

  get isActive(): boolean {
    return this.active;
  }

  /**
   * Cinematic approach: arc outward slightly then ease into a viewing distance
   * proportional to body size, looking at the body.
   */
  goTo(body: CelestialBody, onComplete?: () => void): void {
    this.onComplete = onComplete;
    this.startPos.copy(this.camera.position);
    this.startQuat.copy(this.camera.quaternion);

    const center = body.worldPosition.clone();
    const r = body.radius;
    // Viewing distance: sit outside atmosphere / rings with scale awareness
    const viewDist = Math.max(r * 4.5, r + body.collisionRadius * 0.5 + r * 2.2);
    // Approach from a direction that preserves some travel sense
    const fromDir = this.tmp.copy(this.startPos).sub(center);
    if (fromDir.lengthSq() < 1e-6) {
      fromDir.set(1, 0.3, 0.2);
    }
    fromDir.normalize();
    // Slight elevation for cinematic framing
    fromDir.y += 0.25;
    fromDir.normalize();

    this.endPos.copy(center).addScaledVector(fromDir, viewDist);
    this.lookTarget.copy(center);

    // Midpoint: pull back along path for an arc
    const travel = this.startPos.distanceTo(this.endPos);
    const lift = Math.min(travel * 0.18, Math.max(r * 20, 80));
    this.midPos.lerpVectors(this.startPos, this.endPos, 0.45);
    this.midPos.y += lift * 0.35;
    // Push mid slightly away from body so we don't clip
    this.tmp.copy(this.midPos).sub(center);
    const midClear = body.collisionRadius * 1.5 + r * 2;
    if (this.tmp.length() < midClear) {
      this.tmp.normalize().multiplyScalar(midClear);
      this.midPos.copy(center).add(this.tmp);
    }

    // End orientation: look at body
    const m = new THREE.Matrix4().lookAt(this.endPos, center, new THREE.Vector3(0, 1, 0));
    this.endQuat.setFromRotationMatrix(m);

    // Duration scales with log of travel distance — distant leaps feel longer, not endless
    this.duration = THREE.MathUtils.clamp(1.1 + Math.log10(1 + travel / 50) * 1.6, 1.2, 5.5);
    this.t = 0;
    this.active = true;
    this.controls.enabled = false;
    this.controls.velocity.set(0, 0, 0);
  }

  update(dt: number): boolean {
    if (!this.active) return false;
    this.t += dt / this.duration;
    const u = Math.min(1, this.t);
    const e = easeInOutCubic(u);

    // Quadratic Bezier: start → mid → end
    const omt = 1 - e;
    this.camera.position
      .set(0, 0, 0)
      .addScaledVector(this.startPos, omt * omt)
      .addScaledVector(this.midPos, 2 * omt * e)
      .addScaledVector(this.endPos, e * e);

    // Slerp look
    this.camera.quaternion.slerpQuaternions(this.startQuat, this.endQuat, easeInOutQuad(u));
    this.controls.setLookFromQuaternion(this.camera.quaternion);

    // Soft speed tier for arrival (near-surface relative to body size)
    if (u > 0.7) {
      const targetTier = THREE.MathUtils.clamp(
        4 + Math.log2(Math.max(this.lookTarget.distanceTo(this.camera.position), 1)) * 0.35,
        3,
        14,
      );
      this.controls.speedTier = THREE.MathUtils.lerp(
        this.controls.speedTier,
        targetTier,
        0.08,
      );
    }

    if (u >= 1) {
      this.active = false;
      this.controls.enabled = true;
      this.controls.velocity.set(0, 0, 0);
      this.onComplete?.();
      return true;
    }
    return false;
  }
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
